import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import CachedIcon from '@material-ui/icons/Cached';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';
import { FieldArray, useFormikContext } from 'formik';
import React from 'react';
import {
  CustomSwitchWithText,
  FieldLabel,
  SectionTitle
} from '../components/common';
import useStyles from '../styles';
import { createFuelCalibrationPoint } from '../utils';

export default function FuelSensorSection({
  canConfigure,
  isCarType,
  oilLevel,
  onRefresh,
  onSensorTypeChange,
  isSensorEnabled,
  isSensorModelSupported,
  sensorModel,
  sensorToggling,
  onToggleSensor,
  onChangeSensorModel
}) {
  const classes = useStyles();
  const {
    values,
    touched,
    errors,
    handleBlur,
    handleChange
  } = useFormikContext();

  return (
    <Box className={classes.sectionCard}>
      <SectionTitle title="Cấu hình cảm biến xăng dầu" />
      {canConfigure ? (
        <Grid container spacing={3}>
          {isCarType ? (
            <>
              <Grid item md={6} xs={12}>
                <Box
                  className={`${classes.settingItem} ${classes.settingItemFill}`}
                >
                  <FieldLabel className={classes.settingLabel}>
                    Cảm biến xăng dầu
                  </FieldLabel>
                  <CustomSwitchWithText
                    checked={Boolean(isSensorEnabled)}
                    onChange={onToggleSensor}
                    disabled={sensorToggling}
                    name="isOilSensorEnabled"
                    inputProps={{ 'aria-label': 'oil sensor' }}
                  />
                </Box>
              </Grid>

              {isSensorEnabled && isSensorModelSupported && (
                <Grid item md={6} xs={12}>
                  <Box
                    className={`${classes.settingItem} ${classes.settingItemFill}`}
                  >
                    <FieldLabel className={classes.settingLabel}>
                      Loại cảm biến
                    </FieldLabel>
                    <RadioGroup
                      row
                      name="fuelSensorModel"
                      value={String(sensorModel)}
                      onChange={onChangeSensorModel}
                    >
                      <FormControlLabel
                        value="1"
                        disabled={sensorToggling}
                        control={<Radio color="primary" size="small" />}
                        label="Soji"
                      />
                      <FormControlLabel
                        value="2"
                        disabled={sensorToggling}
                        control={<Radio color="primary" size="small" />}
                        label="Omnicomm"
                      />
                    </RadioGroup>
                  </Box>
                </Grid>
              )}

              {isSensorEnabled && (
                <Grid item xs={12}>
                  <Box className={classes.fuelSensorPanel}>
                    <Box className={classes.fuelSensorHeader}>
                      <FormControl component="fieldset">
                        <RadioGroup
                          row
                          name="fuelSensorType"
                          value={values.fuelSensorType}
                          onChange={e => onSensorTypeChange(e.target.value)}
                        >
                          <FormControlLabel
                            value="pulse"
                            control={<Radio color="primary" />}
                            label="Xung (0 - 4095)"
                          />
                          <FormControlLabel
                            value="percent"
                            control={<Radio color="primary" />}
                            label="Phần trăm (0 - 100%)"
                          />
                        </RadioGroup>
                      </FormControl>

                      <Box className={classes.sensorValueBox}>
                        <Typography className={classes.sensorValueText}>
                          {`Giá trị cảm biến nhiên liệu: ${oilLevel ?? '--'}`}
                        </Typography>
                        <Tooltip title="Làm mới">
                          <IconButton
                            onClick={onRefresh}
                            className={classes.refreshButton}
                          >
                            <CachedIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>

                    <FieldArray name="fuel_calibration_points">
                      {({ push, remove }) => (
                        <>
                          {values.fuel_calibration_points.map(
                            (point, index) => (
                              <Box
                                key={`fuel-calibration-${index}`}
                                className={classes.fuelCalibrationRow}
                              >
                                <TextField
                                  className={`${classes.fuelCalibrationInput} ${classes.textField}`}
                                  name={`fuel_calibration_points.${index}.percent`}
                                  size="small"
                                  placeholder="Giá trị cảm biến"
                                  onBlur={handleBlur}
                                  onChange={handleChange}
                                  value={point.percent}
                                  variant="outlined"
                                  type="number"
                                  error={Boolean(
                                    touched.fuel_calibration_points?.[index]
                                      ?.percent &&
                                      errors.fuel_calibration_points?.[index]
                                        ?.percent
                                  )}
                                  helperText={
                                    (touched.fuel_calibration_points?.[index]
                                      ?.percent &&
                                      errors.fuel_calibration_points?.[index]
                                        ?.percent) ||
                                    ' '
                                  }
                                  inputProps={{
                                    min: 0,
                                    max:
                                      values.fuelSensorType === 'pulse'
                                        ? 4095
                                        : 100
                                  }}
                                  InputProps={{
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        {values.fuelSensorType === 'pulse'
                                          ? ''
                                          : '%'}
                                      </InputAdornment>
                                    )
                                  }}
                                />

                                <TextField
                                  className={`${classes.fuelCalibrationInput} ${classes.textField}`}
                                  name={`fuel_calibration_points.${index}.liters`}
                                  size="small"
                                  placeholder="Dung tích"
                                  onBlur={handleBlur}
                                  onChange={handleChange}
                                  value={point.liters}
                                  variant="outlined"
                                  error={Boolean(
                                    touched.fuel_calibration_points?.[index]
                                      ?.liters &&
                                      errors.fuel_calibration_points?.[index]
                                        ?.liters
                                  )}
                                  helperText={
                                    (touched.fuel_calibration_points?.[index]
                                      ?.liters &&
                                      errors.fuel_calibration_points?.[index]
                                        ?.liters) ||
                                    ' '
                                  }
                                  InputProps={{
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        Lít
                                      </InputAdornment>
                                    )
                                  }}
                                />

                                <IconButton
                                  className={classes.removeCalibrationButton}
                                  onClick={() => remove(index)}
                                  size="small"
                                  type="button"
                                >
                                  <RemoveCircleOutlineIcon />
                                </IconButton>
                              </Box>
                            )
                          )}

                          <Button
                            variant="outlined"
                            fullWidth
                            type="button"
                            onClick={() => push(createFuelCalibrationPoint())}
                            className={classes.addCalibrationButton}
                            startIcon={<AddIcon />}
                          >
                            Thêm mức nhiên liệu
                          </Button>
                        </>
                      )}
                    </FieldArray>

                    {typeof errors.fuel_calibration_points === 'string' && (
                      <Typography className={classes.inlineErrorText}>
                        {errors.fuel_calibration_points}
                      </Typography>
                    )}

                    <Box className={classes.sensorHint}>
                      (L: Giá trị cảm biến do thiết bị gửi lên, V: Dung tích
                      thực của bình)
                    </Box>
                  </Box>
                </Grid>
              )}
            </>
          ) : (
            <Grid item xs={12}>
              <Typography className={classes.emptyTabText}>
                Thiết bị này không hỗ trợ cấu hình cảm biến xăng dầu.
              </Typography>
            </Grid>
          )}
        </Grid>
      ) : (
        <Typography className={classes.emptyTabText}>
          Vui lòng liên hệ với bộ phận kĩ thuật để được hỗ trợ cấu hình cảm biến
          xăng dầu.
        </Typography>
      )}
    </Box>
  );
}
